const cities = [
  { name: "Wellington", zips: "33414" },
  { name: "Royal Palm Beach", zips: "33411" },
  { name: "Loxahatchee Groves", zips: "33470" },
  { name: "Greenacres", zips: "33413" },
  { name: "Lake Worth Beach", zips: "33460" },
  { name: "West Palm Beach", zips: "33401" },
  { name: "Palm Beach Gardens", zips: "33418" },
  { name: "Boca Raton", zips: "33431" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or the barn in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function WellingtonLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Wellington &amp; Western Palm Beach County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Wellington, Royal Palm Beach, Loxahatchee Groves, or anywhere in western Palm Beach County, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Wellington is not like other South Florida communities. Where most Palm Beach County towns evolved around tourism, retail, or coastal access, Wellington was built from the ground up around a singular passion: equestrian sport. Known internationally as the "Equestrian Capital of the World," this western Palm Beach County village of roughly 60,000 residents transforms every winter into the epicenter of global horse culture. The Winter Equestrian Festival — a 12-week showcase of show jumping, dressage, and hunter competition — draws Olympic-level athletes, world-class trainers, and equestrian families from across the United States and Europe. The International Polo Club hosts high-goal polo matches that attract spectators, players, and sponsors from Argentina, England, Mexico, and beyond.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Wellington truly unique is the density of international travel that equestrian life demands. Trainers fly to Europe to scout horses at auctions in Belgium and the Netherlands. Competitors travel to England for the Royal Windsor Horse Show, to France for the Longines Global Champions Tour, and to Germany for the CHIO Aachen. Polo teams rotate players from Argentina and the UK during the winter season. Veterinarians, farriers, grooms, and support staff move across borders with the same regularity that most Americans commute across county lines. For Wellington residents, international travel is not an occasional luxury — it is an operational necessity.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Beyond the equestrian world, Wellington is home to some of the most exclusive gated communities in South Florida: Arden, Binks Forest, Olympia, and Stone Creek Ranch among them. Wellington Green serves as the retail and dining anchor for families who live in these communities, while Wellington Regional Medical Center anchors a growing medical corridor that draws healthcare professionals from across the region. The village is notably more family-oriented and residential than coastal Palm Beach County cities — residents here prioritize excellent schools, safe neighborhoods, and access to the equestrian lifestyle over beach proximity.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Wellington's travel demographics split into distinct but overlapping groups. The equestrian community — trainers, riders, owners, grooms, and support staff — travels to Europe, South America, and Canada for competitions, horse purchases, and training. These trips are often planned on short notice: a horse becomes available at auction, a competition slot opens, or a team needs to rotate players mid-season. Affluent families in Wellington's gated communities travel to the Caribbean, Europe, and Latin America for vacations, often booking on the same timelines as other South Florida families but with higher expectations for service and flexibility. Seasonal residents and snowbirds who own Wellington properties may discover passport issues when transitioning between their northern homes and international travel plans.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic reality of Wellington also matters. Located roughly 15 miles west of downtown West Palm Beach and 20 miles from Palm Beach International Airport (PBI), Wellington is not a quick drive to any passport agency. The closest regional passport agency is in Miami, approximately 80 miles south on the Florida Turnpike or I-95 — a drive that can easily consume two hours in South Florida traffic. For a Wellington trainer who discovers an expired passport the week before a flight to Brussels, that drive is not just inconvenient — it may be impossible given barn and competition schedules.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Wellington travelers across every equestrian and residential demographic. We understand that a trainer flying PBI to Amsterdam for a horse auction has the same urgency as a family of four leaving on a Caribbean cruise. Our courier-based expedited service means you start online from your Wellington estate, your equestrian center office, or your Wellington Green shopping trip — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Wellington — approximately 25 miles east via Southern Blvd / I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Palm Beach County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or Wellington equestrian facility in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Palm Beach County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "West Palm Beach", href: "/west-palm-beach" },
                  { name: "Palm Beach Gardens", href: "/palm-beach-gardens" },
                  { name: "Jupiter", href: "/jupiter" },
                  { name: "Boca Raton", href: "/boca-raton" },
                  { name: "Delray Beach", href: "/delray-beach" },
                  { name: "Palm Beach", href: "/palm-beach" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}